import * as React from 'react';
import { Component, Fragment } from 'react';
import { computed } from 'mobx';
import { observer } from 'mobx-react';
import { inject } from '../utils';
import { withTheme } from 'styled-components';
import { styled } from '../utils/styled';
import { propStyles } from '../utils/propStyles';
import FundCardHeader from './FundCardHeader';
import FundCardTab from './FundCardTab';
import FundCardColumn from './FundCardColumn';
import FundManagersOrchestration from './FundManagersOrchestration';
import { isNullOrEmpty } from '@kurtosys/ksys-app-template/dist/utils/typeChecks';
import RatingImage from './RatingImage';
import FundManagers from './FundManagers';
import { RatingImageStore, DataStore, EntityStore } from '../stores';
import { ChartStore } from '../stores';

const FundCardWrapper = styled<any, any>('div', 'fundCardWrapper', 'fundCard')`
    ${propStyles};
`;

FundCardWrapper.defaultProps = {
    bgColor: 'white',
};
interface FundCardProps {
    [key: string]: any;
    dataStore?: DataStore;
    chartStores?: Map<string, ChartStore>;
    entityStore?: EntityStore;
}

@inject('dataStore', 'entityStore')
@observer
class FundCard extends Component<FundCardProps, any> {
    @computed
    get dataStore() {
        return this.props.dataStore;
    }

    @computed
    get config() {
        return this.dataStore.config;
    }

    @computed
    get header() {
        return this.config.rawRowConfig.header;
    }

    @computed
    get fundCommentary() {
        return this.config.rawRowConfig.fundCommentary;
    }

    @computed
    get hasFundCommentary() {
        let result = false;
        if (
            this.fundCommentary &&
            Object.keys(this.fundCommentary).length > 0 &&
            this.props.entityStore.value?.commentaries
        ) {
            const commentaries = this.props.entityStore.value.commentaries;
            const fundCommentaries = this.fundCommentary.commentaries;
            for (const fundCommentary of fundCommentaries) {
                const commentary = commentaries[fundCommentary.commentaryType];
                if (commentary) {
                    // We need to find at least one commentary that should render
                    result = true;
                    break;
                }
            }
        }
        return result;
    }

    @computed
    get fundFacts() {
        return this.config.rawRowConfig.fundFacts;
    }

    @computed
    get fundManagersOrchestrationConfig() {
        return this.config.rawRowConfig.fundManagersOrchestration;
    }

    @computed
    get fundManagersConfig() {
        return this.config.rawRowConfig.fundManagers || {};
    }

    @computed
    get fundManagers() {
        return (
            (Object.keys(this.fundManagersConfig).length > 0 &&
                this.props.entityStore.value?.fundManagers) ||
            []
        );
    }

    @computed
    get fundManagersComponent() {
        const { fundManagers, fundManagersOrchestrationConfig } = this;
        const { entityStore } = this.props;

        if (entityStore.value) {
            if (!isNullOrEmpty(fundManagersOrchestrationConfig)) {
                return <FundManagersOrchestration />;
            }
            if (!isNullOrEmpty(fundManagers)) {
                return (
                    <FundManagers
                        showHeading={true}
                        fundManagers={fundManagers}
                    />
                );
            }
        }
    }

    @computed
    get ratingImageStore() {
        const { dataStore, entityStore } = this.props;
        return new RatingImageStore(dataStore.stores, entityStore.value);
    }

    @computed
    get ratingImageComponent(): React.ReactElement {
        if (this.ratingImageStore.showComponent) {
            return <RatingImage ratingImageStore={this.ratingImageStore} />;
        }
    }

    @computed
    get keyDocuments() {
        const { documentTypes = [] } = this.config.rawRowConfig
            .keyDocuments as any;
        return documentTypes
            .map(d => {
                // Config has documentTypes as string[] yet we destructuring here???
                const { performanceCheck, documentType } = d;

                if (
                    performanceCheck &&
                    !this.props.entityStore.hasPerformance
                ) {
                    return undefined;
                }

                return this.props.entityStore.groupedDocuments.find(
                    g => g.documentGroupName === (documentType || d)
                );
            })
            .filter(d => d !== undefined);
    }

    // TODO: All the cardColumnProps and cardTabProps seem specific to the Fund Card. Possibly create Fund Card store?
    @computed
    get cardColumnProps() {
        // Builds up the different components into props that can be passed down
        const props = {
            ratingImageComponent: this.ratingImageComponent,
            keyDocuments: this.keyDocuments,
            charts: this.config.rowChartsConfig,
            fundManagers: this.fundManagers,
            fundManagersComponent: this.fundManagersComponent,
            fundCommentary: this.fundCommentary,
            hasFundCommentary: this.hasFundCommentary,
            fundFacts: this.fundFacts,
            ratingImageStore: this.ratingImageStore,
        };

        return props;
    }

    @computed
    get cardTabProps() {
        // Builds up the different components into props that can be passed down
        const props = {
            ...this.cardColumnProps,
            fundManagersConfig: this.fundManagersConfig,
            fundManagersOrchestrationConfig: this
                .fundManagersOrchestrationConfig,
        };

        return props;
    }

    render() {
        const { header } = this;
        const { theme, dataStore } = this.props;

        return (
            <FundCardWrapper {...theme.FundCardWrapper}>
                <Fragment>
                    {header && <FundCardHeader header={header} />}
                    {dataStore.isMobile ? (
                        <FundCardTab {...this.cardTabProps} />
                    ) : (
                        <FundCardColumn {...this.cardColumnProps} />
                    )}
                </Fragment>
            </FundCardWrapper>
        );
    }
}

export default withTheme(FundCard) as any;



// WEBPACK FOOTER //
// ./src/Components/FundCard.tsx